import { THIS_EXPR } from '@angular/compiler/src/output/output_ast';
import { Injectable } from '@angular/core';
import { IUser, UserPermissionResponse } from '@shared/models/login.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from 'src/app/core/http/http.service';
import { UnsubscribeOnDestroyAdapter } from 'src/app/core/unsuscribe-adapter/unsuscribe-adapter';
import { IModuleUserPermission } from 'src/app/main/users/models/user-privileges.model';

@Injectable({
  providedIn: 'root'
})
export class UserService extends UnsubscribeOnDestroyAdapter {
  private readonly USER_DATA = 'USER_DATA';
  private readonly PERMISSIONS = 'PERMISSIONS';
  private readonly USER_MODULES = 'USER_MODULES';
  private readonly LAST_LIST = 'LAST_LIST';

  private user$: BehaviorSubject<IUser>;
  private permissions$: BehaviorSubject<UserPermissionResponse>;
  private modules$: BehaviorSubject<IModuleUserPermission>;

  public user: Observable<IUser>;
  public permissions: Observable<UserPermissionResponse>;
  public modules: Observable<IModuleUserPermission>;

  private lastVisitedList: string;

  constructor(private service: HttpService) {
    super();
    const user = localStorage.getItem(this.USER_DATA);
    const permission = localStorage.getItem(this.PERMISSIONS);
    const modules = localStorage.getItem(this.USER_MODULES);
    const lastList = localStorage.getItem(this.LAST_LIST);
    this.user$ = new BehaviorSubject<IUser>(user ? JSON.parse(user) : null);
    this.permissions$ = new BehaviorSubject<UserPermissionResponse>(
      permission ? JSON.parse(atob(permission)) : null
    );
    this.modules$ = new BehaviorSubject<IModuleUserPermission>(
      modules ? JSON.parse(atob(modules)) : null
    );
    this.lastVisitedList = lastList ? lastList : null;
    this.user = this.user$.asObservable();
    this.permissions = this.permissions$.asObservable();
    this.modules = this.modules$.asObservable();
  }

  set userData(user: IUser) {
    this.user$.next(user);
    localStorage.setItem(this.USER_DATA, JSON.stringify(user));
  }

  get userInfo(): IUser {
    return JSON.parse(JSON.stringify(this.user$.value));
  }

  set userPermissionData(permission: UserPermissionResponse) {
    this.permissions$.next(permission);
    localStorage.setItem(this.PERMISSIONS, btoa(JSON.stringify(permission)));
  }

  get userPermissionInfo(): UserPermissionResponse {
    return this.permissions$.value;
  }

  set userModulesData(modules: IModuleUserPermission) {
    this.modules$.next(modules);
    localStorage.setItem(this.USER_MODULES, btoa(JSON.stringify(modules)));
  }

  get userModulesInfo(): IModuleUserPermission {
    return this.modules$.value;
  }

  set lastListData(list: string) {
    this.lastVisitedList = list;
    localStorage.setItem(this.LAST_LIST, list);
  }

  get lastListConverted(): string {
    return this.lastVisitedList ? this.lastVisitedList.replace(' ', '_') : null;
  }

  getUserPermissions(): void {
    const userData = this.user$.value;
    if (userData) {
      this.subs.add(
        this.service
          .get<UserPermissionResponse>(`permission/${userData.id}`, {})
          .subscribe((data: UserPermissionResponse) => {
            const permissions =
              data && Object.keys(data).length
                ? data
                : {
                    id: null,
                    user_id: userData.id,
                    update_order: {
                      not_signed: false,
                      dispatched: false,
                      in_transit: false,
                      delivered: false,
                      open_invoice: false,
                      damage: false,
                      paid_bills: false,
                      only_heavy_haul: false,
                      only_automotive: false
                    },
                    dispatch_privileges: {
                      perform_dispatch: false,
                      edit_dispatch: false,
                      use_carrier_match: false
                    },
                    payment_privileges: {
                      process_payment: false,
                      edit_carrier_terms: false
                    },
                    reports: false
                  };
            this.permissions$.next(permissions);
            localStorage.setItem(
              this.PERMISSIONS,
              btoa(JSON.stringify(permissions))
            );
          })
      );
    }
  }
}
